import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  CardFieldValueListModel,
  CardFieldValueModel,
  UpsertCardFieldValueRequestModel,
} from '../models/card-details-api.models';
import { asArray, asRecord, asString } from './card-api.utils';

@Injectable({ providedIn: 'root' })
export class CardFieldValuesApiService {
  private readonly http = inject(HttpClient);

  async getByCard(cardId: string): Promise<CardFieldValueListModel> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/card-field-values/card/${cardId}`)
    );
    return { items: this.parseFieldValuesList(response, cardId) };
  }

  /** GET /api/cards/{id}/detail (`fieldValues`) ou listagem dedicada — mesmo mapeamento que `getByCard`. */
  parseFieldValuesList(raw: unknown, cardId: string): CardFieldValueModel[] {
    return asArray(raw)
      .map((entry) => this.mapFieldValue(entry, cardId))
      .filter((entry) => !!entry.workflowFieldId);
  }

  async create(payload: UpsertCardFieldValueRequestModel): Promise<void> {
    await firstValueFrom(this.http.post('/api/card-field-values', this.toRequestPayload(payload)));
  }

  async update(payload: UpsertCardFieldValueRequestModel): Promise<void> {
    await firstValueFrom(this.http.put('/api/card-field-values', this.toRequestPayload(payload)));
  }

  async delete(cardId: string, workflowFieldId: string): Promise<void> {
    try {
      await firstValueFrom(
        this.http.delete(`/api/card-field-values/card/${cardId}/field/${workflowFieldId}`)
      );
    } catch (error) {
      // Endpoint pode retornar 404 quando o valor ja foi removido.
      if (error instanceof HttpErrorResponse && error.status === 404) return;
      throw error;
    }
  }

  private mapFieldValue(raw: unknown, cardId: string): CardFieldValueModel {
    const source = asRecord(raw);
    const nestedField = asRecord(source['field']);
    const nestedWorkflowField = asRecord(source['workflowField']);
    return {
      id: this.coerceIdString(source['id']) || this.coerceIdString(source['Id']),
      cardId: this.coerceIdString(source['cardId']) || this.coerceIdString(source['CardId']) || cardId,
      workflowFieldId:
        this.coerceIdString(source['workflowFieldId']) ||
        this.coerceIdString(source['WorkflowFieldId']) ||
        this.coerceIdString(source['fieldId']) ||
        this.coerceIdString(source['FieldId']) ||
        this.coerceIdString(source['workflowCustomFieldId']) ||
        this.coerceIdString(nestedField['id']) ||
        this.coerceIdString(nestedWorkflowField['id']),
      value: this.readRawValue(source),
    };
  }

  /**
   * Contrato API (SaveCardFieldValueRequest): corpo plano; `value` sempre string serializada.
   */
  private toRequestPayload(payload: UpsertCardFieldValueRequestModel): Record<string, unknown> {
    return {
      cardId: payload.cardId,
      workflowFieldId: payload.workflowFieldId,
      value: this.serializeValueForApi(payload.value) ?? '',
    };
  }

  private serializeValueForApi(
    value: string | number | boolean | string[] | null
  ): string | null {
    if (value == null) return null;
    if (typeof value === 'string') {
      const normalized = this.normalizeOutgoingStringValue(value);
      return normalized && normalized.length ? normalized : null;
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
    if (Array.isArray(value)) {
      const joined = value.map((entry) => String(entry).trim()).filter(Boolean).join(',');
      return joined.length ? joined : null;
    }
    return String(value);
  }

  private normalizeOutgoingStringValue(value: string): string {
    let text = value.trim();
    if (!text) return '';
    // Corrige caso venha duplamente serializado: "\"{...}\"" -> "{...}"
    if (text.startsWith('"') && text.endsWith('"')) {
      try {
        const parsed = JSON.parse(text) as unknown;
        if (typeof parsed === 'string') {
          text = parsed.trim();
        }
      } catch {
        /* mantém texto original */
      }
    }
    if (
      (text.startsWith('{') && text.endsWith('}')) ||
      (text.startsWith('[') && text.endsWith(']'))
    ) {
      try {
        const parsed = JSON.parse(text) as unknown;
        return JSON.stringify(parsed);
      } catch {
        return text;
      }
    }
    return text;
  }


  private readRawValue(source: Record<string, unknown>): string | number | boolean | string[] | null {
    const directCandidates: unknown[] = [
      source['value'],
      source['Value'],
      source['stringValue'],
      source['StringValue'],
      source['textValue'],
      source['longTextValue'],
      source['numberValue'],
      source['NumberValue'],
      source['decimalValue'],
      source['DecimalValue'],
      source['valueDecimal'],
      source['ValueDecimal'],
      source['currencyValue'],
      source['boolValue'],
      source['booleanValue'],
      source['dateValue'],
      source['optionValue'],
      source['optionId'],
      source['fileName'],
      source['fileUrl'],
      source['assetUrl'],
      source['url'],
      source['path'],
      source['userId'],
    ];

    for (const candidate of directCandidates) {
      const parsed = this.parseUnknownValue(candidate);
      if (parsed !== null) return parsed;
    }

    const valueJson = source['valueJson'];
    if (typeof valueJson === 'string' && valueJson.trim()) {
      try {
        const parsedJson = JSON.parse(valueJson) as unknown;
        return this.parseUnknownValue(parsedJson);
      } catch {
        return valueJson;
      }
    }

    return null;
  }

  private parseUnknownValue(value: unknown): string | number | boolean | string[] | null {
    if (value == null) return null;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    if (typeof value === 'number') return value;
    if (typeof value === 'boolean') return value;
    if (Array.isArray(value)) {
      const asStrings = value
        .map((entry) => (typeof entry === 'string' || typeof entry === 'number' ? String(entry) : ''))
        .filter((entry) => entry.length > 0);
      return asStrings.length ? asStrings : null;
    }

    if (typeof value !== 'object') return null;
    const record = asRecord(value);

    const fileLikeObject =
      typeof record['fileName'] === 'string' ||
      typeof record['url'] === 'string' ||
      typeof record['assetUrl'] === 'string' ||
      typeof record['fileUrl'] === 'string' ||
      typeof record['path'] === 'string' ||
      typeof record['storageKey'] === 'string';
    if (fileLikeObject) {
      const compact = {
        ...(typeof record['fileName'] === 'string' ? { fileName: record['fileName'] } : {}),
        ...(typeof record['url'] === 'string' ? { url: record['url'] } : {}),
        ...(typeof record['assetUrl'] === 'string' ? { assetUrl: record['assetUrl'] } : {}),
        ...(typeof record['fileUrl'] === 'string' ? { fileUrl: record['fileUrl'] } : {}),
        ...(typeof record['path'] === 'string' ? { path: record['path'] } : {}),
        ...(typeof record['storageKey'] === 'string' ? { storageKey: record['storageKey'] } : {}),
      };
      return JSON.stringify(compact);
    }

    const nestedCandidates: unknown[] = [
      record['value'],
      record['name'],
      record['label'],
      record['id'],
      record['fileName'],
      record['url'],
      record['key'],
    ];
    for (const candidate of nestedCandidates) {
      const parsed = this.parseUnknownValue(candidate);
      if (parsed !== null) return parsed;
    }

    const nestedArrayCandidates: unknown[] = [record['values'], record['items'], record['selectedValues']];
    for (const candidate of nestedArrayCandidates) {
      const parsed = this.parseUnknownValue(candidate);
      if (parsed !== null) return parsed;
    }

    return null;
  }

  private coerceIdString(value: unknown): string {
    if (typeof value === 'string') {
      const t = value.trim();
      return t;
    }
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return '';
  }
}
